import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Keyboard,
  ChevronDown,
  ChevronUp,
  Users,
} from 'lucide-react';

// ==================== CONSTANTS ====================

const ARABIC_KEYBOARD_LAYOUT = [
  ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج'],
  ['ش', 'س', 'ي', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ك', 'ط'],
  ['ئ', 'ء', 'ؤ', 'ر', 'لا', 'ى', 'ة', 'و', 'ز', 'ظ', 'د'],
];

const COUNTRY_PHONE_CODES = [
  { code: 'MA', name: 'Maroc', dialCode: '+212', flag: '🇲🇦' },
  { code: 'DZ', name: 'Algérie', dialCode: '+213', flag: '🇩🇿' },
  { code: 'TN', name: 'Tunisie', dialCode: '+216', flag: '🇹🇳' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'ES', name: 'Espagne', dialCode: '+34', flag: '🇪🇸' },
  { code: 'BE', name: 'Belgique', dialCode: '+32', flag: '🇧🇪' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'US', name: 'États-Unis', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'Royaume-Uni', dialCode: '+44', flag: '🇬🇧' },
  { code: 'DE', name: 'Allemagne', dialCode: '+49', flag: '🇩🇪' },
  { code: 'IT', name: 'Italie', dialCode: '+39', flag: '🇮🇹' },
  { code: 'NL', name: 'Pays-Bas', dialCode: '+31', flag: '🇳🇱' },
  { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹' },
  { code: 'SA', name: 'Arabie Saoudite', dialCode: '+966', flag: '🇸🇦' },
  { code: 'AE', name: 'Émirats Arabes Unis', dialCode: '+971', flag: '🇦🇪' },
  { code: 'EG', name: 'Égypte', dialCode: '+20', flag: '🇪🇬' },
  { code: 'SN', name: 'Sénégal', dialCode: '+221', flag: '🇸🇳' },
  { code: 'CI', name: "Côte d'Ivoire", dialCode: '+225', flag: '🇨🇮' },
  { code: 'ML', name: 'Mali', dialCode: '+223', flag: '🇲🇱' },
  { code: 'MR', name: 'Mauritanie', dialCode: '+222', flag: '🇲🇷' },
];

// Relation options for parent/guardian
const RELATION_OPTIONS = [
  { value: 'PERE', label: 'Père' },
  { value: 'MERE', label: 'Mère' },
  { value: 'TUTEUR_LEGAL', label: 'Tuteur légal' },
  { value: 'GRAND_PARENT', label: 'Grand-parent' },
  { value: 'ONCLE_TANTE', label: 'Oncle / Tante' },
  { value: 'AUTRE', label: 'Autre' },
];

// Relations that require specification
const RELATIONS_REQUIRING_PRECISION = ['TUTEUR_LEGAL', 'AUTRE'];

// ==================== HELPER FUNCTIONS ====================

// Capitalize first letter of each word (for first names)
const capitalizeFirstLetter = (value: string): string => {
  return value
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// ==================== SCHEMA ====================

const parentProfileSchema = z.object({
  // Identity - French
  nom: z.string().min(2, 'Le nom est requis (min. 2 caractères)'),
  prenom: z.string().min(2, 'Le prénom est requis (min. 2 caractères)'),
  // Identity - Arabic
  nomArabe: z.string().optional(),
  prenomArabe: z.string().optional(),
  // Personal info
  sexe: z.enum(['M', 'F'], { required_error: 'Veuillez sélectionner le sexe' }),
  // Contact
  email: z.string().email('Email invalide').optional().or(z.literal('')),
  telephone: z.string().min(9, 'Le numéro de téléphone est requis'),
  phoneCountry: z.string().default('MA'),
  // Address
  adresse: z.string().min(5, "L'adresse est requise (min. 5 caractères)"),
  // Relation with student
  relationAvecEtudiant: z.string().min(1, 'Veuillez sélectionner la relation avec l\'étudiant'),
  relationPrecision: z.string().optional(),
}).refine((data) => {
  // If relation requires precision, it must be provided
  if (RELATIONS_REQUIRING_PRECISION.includes(data.relationAvecEtudiant)) {
    return data.relationPrecision && data.relationPrecision.trim().length >= 2;
  }
  return true;
}, {
  message: 'Veuillez préciser la relation (min. 2 caractères)',
  path: ['relationPrecision'],
});

export type ParentProfileFormData = z.infer<typeof parentProfileSchema>;

// ==================== INTERFACES ====================

export interface ParentProfileFormHandle {
  submit: () => void;
  getFormData: () => Partial<ParentProfileFormData>;
  isValid: () => boolean;
}

interface ParentProfileFormProps {
  initialData?: Partial<ParentProfileFormData>;
  onSubmit: (data: ParentProfileFormData) => void;
  onSaveProgress?: (data: Partial<ParentProfileFormData>) => void;
  isLoading?: boolean;
  isLocked?: boolean;
}

// ==================== VIRTUAL KEYBOARD ====================

interface VirtualArabicKeyboardProps {
  onKeyPress: (key: string) => void;
  onBackspace: () => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
}

const VirtualArabicKeyboard: React.FC<VirtualArabicKeyboardProps> = ({
  onKeyPress,
  onBackspace,
  onClose,
  anchorRef,
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  React.useEffect(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      });
    }
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <div
      className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-xl p-3"
      style={{ top: position.top, left: position.left }}
    >
      <div className="flex justify-between items-center mb-2 pb-2 border-b">
        <span className="text-sm font-medium text-gray-700">Clavier Arabe</span>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>
      <div className="space-y-1" dir="rtl">
        {ARABIC_KEYBOARD_LAYOUT.map((row, rowIndex) => (
          <div key={rowIndex} className="flex justify-center gap-1">
            {row.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => onKeyPress(key)}
                className="w-8 h-8 bg-gray-100 hover:bg-primary-100 rounded text-lg font-arabic transition-colors"
              >
                {key}
              </button>
            ))}
          </div>
        ))}
        <div className="flex justify-center gap-2 mt-2 pt-2 border-t">
          <button
            type="button"
            onClick={() => onKeyPress(' ')}
            className="px-8 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm"
          >
            Espace
          </button>
          <button
            type="button"
            onClick={onBackspace}
            className="px-4 py-1 bg-red-100 hover:bg-red-200 rounded text-sm"
          >
            ← Effacer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ==================== ARABIC INPUT COMPONENT ====================

interface ArabicInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
}

const ArabicInput: React.FC<ArabicInputProps> = ({
  label,
  value,
  onChange,
  placeholder,
  error,
  disabled,
}) => {
  const [showKeyboard, setShowKeyboard] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleKeyPress = (key: string) => {
    onChange(value + key);
  };

  const handleBackspace = () => {
    onChange(value.slice(0, -1));
  };

  return (
    <div ref={containerRef} className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-right font-arabic ${
            error ? 'border-red-500' : 'border-gray-300'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          dir="rtl"
        />
        <button
          type="button"
          onClick={() => setShowKeyboard(!showKeyboard)}
          disabled={disabled}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-600 disabled:opacity-50"
        >
          <Keyboard className="w-5 h-5" />
        </button>
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
      {showKeyboard && !disabled && (
        <VirtualArabicKeyboard
          onKeyPress={handleKeyPress}
          onBackspace={handleBackspace}
          onClose={() => setShowKeyboard(false)}
          anchorRef={containerRef}
        />
      )}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

export const ParentProfileForm = forwardRef<ParentProfileFormHandle, ParentProfileFormProps>(
  ({ initialData, onSubmit, onSaveProgress, isLoading = false, isLocked = false }, ref) => {
    const [expandedSections, setExpandedSections] = useState({
      identity: true,
      contact: true,
      relation: true,
    });

    const [showPhoneDropdown, setShowPhoneDropdown] = useState(false);
    const phoneDropdownRef = useRef<HTMLDivElement>(null);
    const phoneButtonRef = useRef<HTMLButtonElement>(null);
    const [phoneDropdownPosition, setPhoneDropdownPosition] = useState({ top: 0, left: 0 });

    const {
      register,
      control,
      handleSubmit,
      watch,
      setValue,
      getValues,
      reset,
      formState: { errors, isValid },
    } = useForm<ParentProfileFormData>({
      resolver: zodResolver(parentProfileSchema),
      defaultValues: {
        nom: initialData?.nom || '',
        prenom: initialData?.prenom || '',
        nomArabe: initialData?.nomArabe || '',
        prenomArabe: initialData?.prenomArabe || '',
        sexe: initialData?.sexe || undefined,
        email: initialData?.email || '',
        telephone: initialData?.telephone || '',
        phoneCountry: initialData?.phoneCountry || 'MA',
        adresse: initialData?.adresse || '',
        relationAvecEtudiant: initialData?.relationAvecEtudiant || '',
        relationPrecision: initialData?.relationPrecision || '',
      },
      mode: 'onChange',
    });

    // Track the last data hash we've seen to detect real changes from storage
    const lastDataHashRef = useRef<string>('');

    // Reset form when initialData changes with new meaningful data from storage
    useEffect(() => {
      if (initialData && Object.keys(initialData).length > 0) {
        // Create a comprehensive hash of the data that matters
        const dataHash = JSON.stringify({
          nom: initialData.nom || '',
          prenom: initialData.prenom || '',
          nomArabe: initialData.nomArabe || '',
          prenomArabe: initialData.prenomArabe || '',
          sexe: initialData.sexe || '',
          email: initialData.email || '',
          telephone: initialData.telephone || '',
          phoneCountry: initialData.phoneCountry || '',
          adresse: initialData.adresse || '',
          relationAvecEtudiant: initialData.relationAvecEtudiant || '',
          relationPrecision: initialData.relationPrecision || '',
        });

        // Only reset if this is genuinely new/different data
        if (dataHash !== lastDataHashRef.current) {
          reset({
            nom: initialData.nom || '',
            prenom: initialData.prenom || '',
            nomArabe: initialData.nomArabe || '',
            prenomArabe: initialData.prenomArabe || '',
            sexe: initialData.sexe || undefined,
            email: initialData.email || '',
            telephone: initialData.telephone || '',
            phoneCountry: initialData.phoneCountry || 'MA',
            adresse: initialData.adresse || '',
            relationAvecEtudiant: initialData.relationAvecEtudiant || '',
            relationPrecision: initialData.relationPrecision || '',
          });
          lastDataHashRef.current = dataHash;
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData]);

    const watchedRelation = watch('relationAvecEtudiant');
    const watchedPhoneCountry = watch('phoneCountry');
    const showRelationPrecision = RELATIONS_REQUIRING_PRECISION.includes(watchedRelation);

    // Update dropdown position when opened
    useEffect(() => {
      if (showPhoneDropdown && phoneButtonRef.current) {
        const rect = phoneButtonRef.current.getBoundingClientRect();
        setPhoneDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
        });
      }
    }, [showPhoneDropdown]);

    // Close dropdown when clicking outside
    useEffect(() => {
      if (!showPhoneDropdown) return undefined;

      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        if (
          phoneDropdownRef.current && !phoneDropdownRef.current.contains(target) &&
          phoneButtonRef.current && !phoneButtonRef.current.contains(target)
        ) {
          setShowPhoneDropdown(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showPhoneDropdown]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(onSubmit)();
      },
      getFormData: () => getValues(),
      isValid: () => isValid,
    }));

    // Auto-save progress
    React.useEffect(() => {
      if (!onSaveProgress) return;

      const subscription = watch((data) => {
        onSaveProgress(data as Partial<ParentProfileFormData>);
      });
      return () => subscription.unsubscribe();
    }, [watch, onSaveProgress]);

    const toggleSection = (section: keyof typeof expandedSections) => {
      setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const selectedPhoneCountry = COUNTRY_PHONE_CODES.find((c) => c.code === watchedPhoneCountry);

    const onFormSubmit = (data: ParentProfileFormData) => {
      onSubmit(data);
    };

    return (
      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
        {/* Identity Section */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection('identity')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary-600" />
              <span className="font-semibold text-gray-900">Identité</span>
            </div>
            {expandedSections.identity ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>

          {expandedSections.identity && (
            <div className="p-6 space-y-6">
              {/* French Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('nom', {
                      onChange: (e) => {
                        e.target.value = e.target.value.toUpperCase();
                      }
                    })}
                    disabled={isLocked || isLoading}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 uppercase ${
                      errors.nom ? 'border-red-500' : 'border-gray-300'
                    } ${isLocked || isLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="Votre nom de famille"
                  />
                  {errors.nom && <p className="text-xs text-red-500">{errors.nom.message}</p>}
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('prenom', {
                      onChange: (e) => {
                        e.target.value = capitalizeFirstLetter(e.target.value);
                      }
                    })}
                    disabled={isLocked || isLoading}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.prenom ? 'border-red-500' : 'border-gray-300'
                    } ${isLocked || isLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="Votre prénom"
                  />
                  {errors.prenom && <p className="text-xs text-red-500">{errors.prenom.message}</p>}
                </div>
              </div>

              {/* Arabic Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Controller
                  name="nomArabe"
                  control={control}
                  render={({ field }) => (
                    <ArabicInput
                      label="الاسم العائلي (Nom en arabe)"
                      value={field.value || ''}
                      onChange={field.onChange}
                      placeholder="اكتب اسمك العائلي"
                      error={errors.nomArabe?.message}
                      disabled={isLocked || isLoading}
                    />
                  )}
                />

                <Controller
                  name="prenomArabe"
                  control={control}
                  render={({ field }) => (
                    <ArabicInput
                      label="الاسم الشخصي (Prénom en arabe)"
                      value={field.value || ''}
                      onChange={field.onChange}
                      placeholder="اكتب اسمك الشخصي"
                      error={errors.prenomArabe?.message}
                      disabled={isLocked || isLoading}
                    />
                  )}
                />
              </div>

              {/* Sexe */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Sexe <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="sexe"
                  control={control}
                  render={({ field }) => (
                    <div className="flex gap-4">
                      <label
                        className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer transition-colors ${
                          field.value === 'M'
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-300 hover:border-gray-400'
                        } ${isLocked || isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="radio"
                          value="M"
                          checked={field.value === 'M'}
                          onChange={() => field.onChange('M')}
                          disabled={isLocked || isLoading}
                          className="sr-only"
                        />
                        <span>Masculin</span>
                      </label>
                      <label
                        className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer transition-colors ${
                          field.value === 'F'
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-300 hover:border-gray-400'
                        } ${isLocked || isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="radio"
                          value="F"
                          checked={field.value === 'F'}
                          onChange={() => field.onChange('F')}
                          disabled={isLocked || isLoading}
                          className="sr-only"
                        />
                        <span>Féminin</span>
                      </label>
                    </div>
                  )}
                />
                {errors.sexe && <p className="text-xs text-red-500">{errors.sexe.message}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Contact Section */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection('contact')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-primary-600" />
              <span className="font-semibold text-gray-900">Coordonnées</span>
            </div>
            {expandedSections.contact ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>

          {expandedSections.contact && (
            <div className="p-6 space-y-4">
              {/* Email */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  <Mail className="inline w-4 h-4 mr-1" />
                  Email <span className="text-gray-400 text-xs">(optionnel)</span>
                </label>
                <input
                  type="email"
                  {...register('email')}
                  disabled={isLocked || isLoading}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  } ${isLocked || isLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="votre.email@exemple.com"
                />
                {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
              </div>

              {/* Phone */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  <Phone className="inline w-4 h-4 mr-1" />
                  Téléphone <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  {/* Country Code Dropdown - Uses portal to avoid clipping */}
                  <div className="relative">
                    <button
                      ref={phoneButtonRef}
                      type="button"
                      onClick={() => !isLocked && !isLoading && setShowPhoneDropdown(!showPhoneDropdown)}
                      disabled={isLocked || isLoading}
                      className={`flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 min-w-[100px] ${
                        isLocked || isLoading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <span>{selectedPhoneCountry?.flag}</span>
                      <span className="text-sm">{selectedPhoneCountry?.dialCode}</span>
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    </button>
                    {showPhoneDropdown && ReactDOM.createPortal(
                      <div
                        ref={phoneDropdownRef}
                        className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden min-w-[220px]"
                        style={{ top: phoneDropdownPosition.top, left: phoneDropdownPosition.left }}
                      >
                        <div className="overflow-y-auto max-h-64">
                          {COUNTRY_PHONE_CODES.map((country) => (
                            <button
                              key={country.code}
                              type="button"
                              onClick={() => {
                                setValue('phoneCountry', country.code);
                                setShowPhoneDropdown(false);
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left ${
                                watchedPhoneCountry === country.code ? 'bg-primary-50' : ''
                              }`}
                            >
                              <span>{country.flag}</span>
                              <span className="text-sm">{country.name}</span>
                              <span className="text-xs text-gray-500 ml-auto">{country.dialCode}</span>
                            </button>
                          ))}
                        </div>
                      </div>,
                      document.body
                    )}
                  </div>

                  {/* Phone Number Input */}
                  <input
                    type="tel"
                    {...register('telephone')}
                    disabled={isLocked || isLoading}
                    className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.telephone ? 'border-red-500' : 'border-gray-300'
                    } ${isLocked || isLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="6 12 34 56 78"
                  />
                </div>
                {errors.telephone && <p className="text-xs text-red-500">{errors.telephone.message}</p>}
              </div>

              {/* Address */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  <MapPin className="inline w-4 h-4 mr-1" />
                  Adresse <span className="text-red-500">*</span>
                </label>
                <textarea
                  {...register('adresse')}
                  disabled={isLocked || isLoading}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.adresse ? 'border-red-500' : 'border-gray-300'
                  } ${isLocked || isLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="Votre adresse complète"
                />
                {errors.adresse && <p className="text-xs text-red-500">{errors.adresse.message}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Relation Section */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection('relation')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary-600" />
              <span className="font-semibold text-gray-900">Relation avec l'étudiant</span>
            </div>
            {expandedSections.relation ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>

          {expandedSections.relation && (
            <div className="p-6 space-y-4">
              {/* Relation Type */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Type de relation <span className="text-red-500">*</span>
                </label>
                <select
                  {...register('relationAvecEtudiant')}
                  disabled={isLocked || isLoading}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.relationAvecEtudiant ? 'border-red-500' : 'border-gray-300'
                  } ${isLocked || isLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">Sélectionner...</option>
                  {RELATION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.relationAvecEtudiant && (
                  <p className="text-xs text-red-500">{errors.relationAvecEtudiant.message}</p>
                )}
              </div>

              {/* Relation Precision (shown for TUTEUR_LEGAL or AUTRE) */}
              {showRelationPrecision && (
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Précisez la relation <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('relationPrecision')}
                    disabled={isLocked || isLoading}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.relationPrecision ? 'border-red-500' : 'border-gray-300'
                    } ${isLocked || isLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder={
                      watchedRelation === 'TUTEUR_LEGAL'
                        ? 'Ex: Tuteur désigné par le tribunal, Famille d\'accueil...'
                        : 'Précisez votre relation avec l\'étudiant'
                    }
                  />
                  {errors.relationPrecision && (
                    <p className="text-xs text-red-500">{errors.relationPrecision.message}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </form>
    );
  }
);

ParentProfileForm.displayName = 'ParentProfileForm';

export default ParentProfileForm;
